import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
// import { Logo } from 'Components/logo'; // re-enable with the logo block below
import LightRays from 'Components/reactbits/LightRays';
// Same animated lucide icons the rest of the app uses (settings tiles, content
// toolbars): the glyph's own geometry animates, driven by the row's hover state.
import { AnimatedUserRoundPlus, AnimatedGift } from 'Components/reactbits/AnimatedIcons';
import { Pathname } from 'Routes';
import styles from './Referral.module.css';

// Account-type chooser. Reached either from a shared affiliate link
// (`/referral/:code`) — which carries an affiliate code — or from `/choose`
// with no code. Lets the visitor pick self-signup vs. gifting and routes them
// to the matching flow, preserving the affiliate code only when present.
function Referral() {
  const { code } = useParams();
  // Which tile is hovered — drives its animated icon (same pattern as the
  // settings tiles in the v2 UI).
  const [hovered, setHovered] = useState(null);

  // Same-app signup/login flow — used by both the "Yourself" tile and the
  // "Log in" link. Kept on the current origin so it works across environments.
  // The referral param is appended only when we actually have a code.
  const signInUrl = `${window.location.origin}${Pathname.authentication.newSignUp}${code ? `?referral=${code}` : ''}`;

  const goToSelfSignup = () => {
    window.location.href = signInUrl;
  };

  const goToGift = () => {
    // Gifting lives on the marketing domain, so this is an external redirect.
    window.location.href = `${window.location.origin}/gift${code ? `/${code}` : ''}`;
  };

  // Mirror the browser's back button so it works even when the user landed
  // here directly from a shared link.
  const goBack = () => {
    window.history.back();
  };

  return (
    <div className={styles.page}>
      {/* Decorative backdrop only — same volumetric light rays as the login
          card, fanning down from the top over the near-black base. Fixed and
          pointer-events: none, so it sits behind the existing markup without
          touching it. Props match /newSign-in exactly. */}
      <div className={styles.raysLayer} aria-hidden="true">
        <LightRays
          raysOrigin="top-center"
          raysColor="#ffffff"
          raysSpeed={0.9}
          lightSpread={0.85}
          rayLength={1.25}
          fadeDistance={1.1}
          saturation={1}
          followMouse
          mouseInfluence={0.08}
          noiseAmount={0.08}
          distortion={0.04}
        />
      </div>

      <button type="button" className={styles.backButton} onClick={goBack}>
        <ArrowBackIcon fontSize="inherit" />
        <span>Back</span>
      </button>

      {/* Logo commented out — the login card doesn't show a standalone brand
          mark above the card. Uncomment (with the `Logo` import above and the
          .logoWrap rules in the module) to bring it back.
      <div className={styles.logoWrap}>
        <Logo />
      </div>
      */}

      <div className={styles.card}>
        <h1 className={styles.title}>Create Account For?</h1>
        <p className={styles.subtitle}>Choose how you&apos;d like to get started.</p>

        <div className={styles.options}>
          <div
            className={styles.option}
            role="button"
            tabIndex={0}
            onClick={goToSelfSignup}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') goToSelfSignup(); }}
            onMouseEnter={() => setHovered('self')}
            onMouseLeave={() => setHovered(null)}
            onFocus={() => setHovered('self')}
            onBlur={() => setHovered(null)}
          >
            <span className={styles.optionIcon}>
              <AnimatedUserRoundPlus size={28} strokeWidth={1.9} animate={hovered === 'self'} />
            </span>
            <span className={styles.optionTitle}>Yourself</span>
            <span className={styles.optionDesc}>Set up your own Projector account</span>
          </div>

          <div
            className={styles.option}
            role="button"
            tabIndex={0}
            onClick={goToGift}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') goToGift(); }}
            onMouseEnter={() => setHovered('gift')}
            onMouseLeave={() => setHovered(null)}
            onFocus={() => setHovered('gift')}
            onBlur={() => setHovered(null)}
          >
            <span className={styles.optionIcon}>
              <AnimatedGift size={28} strokeWidth={1.9} animate={hovered === 'gift'} />
            </span>
            <span className={styles.optionTitle}>For Someone Else</span>
            <span className={styles.optionDesc}>Send Projector as a gift to a loved one</span>
          </div>
        </div>

        <p className={styles.loginPrompt}>
          Already have an account?
          <a className={styles.loginLink} href={signInUrl}>Log in</a>
        </p>
      </div>
    </div>
  );
}

export default Referral;
